import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import envConfig from '../envConfig';
import Swal from 'sweetalert2';
import DataTable from 'react-data-table-component';
import { AuthContext } from '../App';
import * as XLSX from 'xlsx';
import { formatToHumanDateTime } from '../utils/DateFormatter';
import { RecipientGroupCard, RecipientModal } from './RecipientPicker';

function RecipientsModal({ row, onClose }) {
    const fmtDate = d => {
        if (!d) return '--';
        const dt = new Date(d);
        return `${dt.toLocaleString('default', { month: 'short' })} ${dt.getDate()}, ${dt.getFullYear()}`;
    };
    return (
        <>
            <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 9998, background: 'rgba(0,0,0,0.4)' }} />
            <div style={{
                position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
                zIndex: 9999, background: '#fff', borderRadius: '6px', width: '580px', maxWidth: '95vw',
                boxShadow: '0 4px 20px rgba(0,0,0,0.25)', overflow: 'hidden',
            }}>
                <div style={{ background: '#2e7d32', color: '#fff', padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                        <div style={{ fontWeight: 'bold', fontSize: '15px' }}>Recipients — {row.adm3}</div>
                        <div style={{ fontSize: '12px', opacity: 0.85, marginTop: '3px' }}>{row.adm1} &rsaquo; {row.adm2} &rsaquo; {row.adm3}</div>
                    </div>
                    <span onClick={onClose} style={{ cursor: 'pointer', fontSize: '20px', lineHeight: 1, marginLeft: '12px' }}>&times;</span>
                </div>
                <div style={{ padding: '8px 16px', fontSize: '12px', background: '#f8f9fa', borderBottom: '1px solid #dee2e6', display: 'flex', gap: '20px' }}>
                    {row.weekly
                        ? <span>Week: <strong>{fmtDate(row.week_start)}</strong> – <strong>{fmtDate(row.week_end)}</strong></span>
                        : <span>Date: <strong>{fmtDate(row.ref_date)}</strong></span>
                    }
                    <span>Count: <strong>{row.recepient_count}</strong></span>
                </div>
                <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                        <thead style={{ position: 'sticky', top: 0, background: '#f1f1f1' }}>
                            <tr>
                                <th style={{ padding: '6px 12px', textAlign: 'left', fontWeight: 600, width: '32px' }}>#</th>
                                <th style={{ padding: '6px 12px', textAlign: 'left', fontWeight: 600 }}>Name</th>
                                <th style={{ padding: '6px 12px', textAlign: 'left', fontWeight: 600 }}>Phone</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(row.recipients || []).map((r, i) => (
                                <tr key={i} style={{ borderTop: '1px solid #f0f0f0' }}>
                                    <td style={{ padding: '4px 10px', color: '#aaa', whiteSpace: 'nowrap' }}>{i + 1}</td>
                                    <td style={{ padding: '4px 10px', whiteSpace: 'nowrap' }}>{r[0]}</td>
                                    <td style={{ padding: '4px 10px', color: '#555', whiteSpace: 'nowrap' }}>{r[1]}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div style={{ padding: '10px 16px', textAlign: 'right', borderTop: '1px solid #dee2e6' }}>
                    <button onClick={onClose} style={{ padding: '5px 18px', background: '#2e7d32', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}>Close</button>
                </div>
            </div>
        </>
    );
}

function RecipientsCell({ row }) {
    const [open, setOpen] = useState(false);
    const count = row.recepient_count;
    if (!count || count === 0) return <span>NA</span>;
    return (
        <span>
            <span>{count}</span>
            {row.recipients && row.recipients.length > 0 && (
                <>
                    <span
                        onClick={() => setOpen(true)}
                        style={{ cursor: 'pointer', marginLeft: '5px', color: '#1565c0', fontWeight: 'bold', fontSize: '13px' }}
                        title="View recipients"
                    >&#9432;</span>
                    {open && <RecipientsModal row={row} onClose={() => setOpen(false)} />}
                </>
            )}
        </span>
    );
}

function HomeDetails({ advisoryData, selectedFilters }) {
    const { isAuthenticated, logout } = useContext(AuthContext);
    const navigate = useNavigate();
    const [advisoryText, setAdvisoryText] = useState('Choose a location to generate advice');
    const [maxTemp, setMaxTemp] = useState(null);
    const [minTemp, setMinTemp] = useState(null);
    const [recentMessageLogs, setRecentMessageLogs] = useState([]);
    const [pageNo, setPageNo] = useState(1);
    const [perPage, setPerPage] = useState(5);
    const [totalRows, setTotalRows] = useState(0);
    const [selectedRows, setSelectedRows] = useState([]);
    const [selectedLanguage, setSelectedLanguage] = useState('English');
    const [localLanguageLabel, setLocalLanguageLabel] = useState('Bemba');
    const [farmersList, setFarmersList] = useState([]);
    const [officersList, setOfficersList] = useState([]);
    const [farmersActive, setFarmersActive] = useState(true);
    const [farmersMode, setFarmersMode] = useState("all");
    const [farmersSelected, setFarmersSelected] = useState(new Set());
    const [officersActive, setOfficersActive] = useState(true);
    const [officersMode, setOfficersMode] = useState("all");
    const [officersSelected, setOfficersSelected] = useState(new Set());
    const [modalOpen, setModalOpen] = useState(null);

    // Weekly recipient state (independent of daily)
    const [weeklyFarmersActive, setWeeklyFarmersActive] = useState(true);
    const [weeklyFarmersMode, setWeeklyFarmersMode] = useState("all");
    const [weeklyFarmersSelected, setWeeklyFarmersSelected] = useState(new Set());
    const [weeklyOfficersActive, setWeeklyOfficersActive] = useState(true);
    const [weeklyOfficersMode, setWeeklyOfficersMode] = useState("all");
    const [weeklyOfficersSelected, setWeeklyOfficersSelected] = useState(new Set());
    const [weeklyModalOpen, setWeeklyModalOpen] = useState(null);

    // Weekly advisory
    const [weeklyAdvisory, setWeeklyAdvisory] = useState(null);
    const [weeklyLanguage, setWeeklyLanguage] = useState('english');
    const [weeklyAdvisoryLoading, setWeeklyAdvisoryLoading] = useState(false);
    const [weeklyAdvisoryText, setWeeklyAdvisoryText] = useState('');
    const [weeklySending, setWeeklySending] = useState(false);

    const getAuthHeaders = () => {
        const token = localStorage.getItem('profishBlue_authToken');
        const expiryTime = localStorage.getItem('profishBlue_tokenExpiry');

        const isTokenExpired = !token || !expiryTime || Date.now() > parseInt(expiryTime, 10);

        if (isTokenExpired) {
            logout();
            window.location.href = '/login';
            return {};
        }

        return {
            'Content-Type': 'application/json',
            Authorization: token,
        };
    };

    const fetchMessages = async (fetchAll = false) => {
        try {
            const url = fetchAll
                ? `${envConfig.API_URL}/advisory/message_log?country_id=${selectedFilters.country_id}&adm3_id=${selectedFilters.adm3_id}`
                : `${envConfig.API_URL}/advisory/message_log?country_id=${selectedFilters.country_id}&adm3_id=${selectedFilters.adm3_id}&page=${pageNo}&size=${perPage}`;

            const response = await fetch(url, {
                method: 'GET',
                headers: getAuthHeaders(),
            });

            if (!response.ok) {
                throw new Error(`HTTP Error: ${response.status}`);
            }

            const result = await response.json();
            if (result.success === 1) {
                if (fetchAll) {
                    return result.data || [];
                } else {
                    setRecentMessageLogs(result.data || []);
                    setTotalRows(result.metadata.total_count || 0);
                }
            } else {
                throw new Error('API request failed');
            }
        } catch (error) {
            Swal.fire('Error', 'Failed to get the logs. Please try again later.', 'error');
            return [];
        }
    };

    useEffect(() => {
        if (selectedFilters?.country_id) {
            const language = (+selectedFilters?.country_id === 2 || selectedFilters?.country_id === undefined) ? 'Bemba' : 'Chichewa';
            setLocalLanguageLabel(language);
            fetchMessages();
        }
    }, [selectedFilters, pageNo, perPage]);

    useEffect(() => {
        if (selectedFilters?.country_id && selectedFilters?.adm3_id) {
            fetchWeeklyAdvisory();
            fetchRecipients(selectedFilters.country_id, selectedFilters.adm3_id);
        } else {
            setWeeklyAdvisory(null);
            setFarmersList([]); setOfficersList([]);
        }
    }, [selectedFilters?.country_id, selectedFilters?.adm3_id]);

    useEffect(() => {
        if (advisoryData) {
            const date = new Date(selectedFilters.date);
            const day = date.getDate().toString().padStart(2, '0');
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            const year = date.getFullYear();
            const formattedDate = `${day}-${month}-${year}`;

            let prefix = '';
            if (selectedLanguage === 'English') {
                prefix = `Advisory for ${formattedDate}\n`;
            } else if (selectedLanguage === 'Bemba') {
                prefix = `Ifunde ya ${formattedDate}\n`;
            } else if (selectedLanguage === 'Chichewa') {
                prefix = `Langizo la ${formattedDate}\n`;
            }

            const text = selectedLanguage === 'English'
                ? advisoryData.advisory_message?.map(line => line.trim()).join('\n') || 'No advisory message available'
                : selectedLanguage === 'Chichewa' && +selectedFilters?.country_id === 2
                    ? advisoryData.advisory_message_local2?.map(line => line.trim()).join('\n') || 'No local advisory message available'
                    : advisoryData.advisory_message_local?.map(line => line.trim()).join('\n') || 'No local advisory message available';

            setAdvisoryText(prefix + text);
            setMaxTemp(advisoryData.temp_max);
            setMinTemp(advisoryData.temp_min);
        } else {
            setAdvisoryText('Choose a location to generate advice');
            setSelectedLanguage('English');
        }
    }, [advisoryData, selectedLanguage, selectedFilters, localLanguageLabel]);

    const fetchWeeklyAdvisory = async () => {
        setWeeklyAdvisoryLoading(true);
        setWeeklyAdvisory(null);
        try {
            const url = `${envConfig.API_URL}/advisory/weekly_advisory?country_id=${selectedFilters.country_id}&adm3_id=${selectedFilters.adm3_id}`;
            const response = await fetch(url, { method: 'GET', headers: getAuthHeaders() });
            if (!response.ok) throw new Error(`HTTP Error: ${response.status}`);
            const result = await response.json();
            if (result.success === 1) {
                setWeeklyAdvisory(result);
                setWeeklyLanguage('english');
            }
        } catch {
            // silently fail — no advisory available
        } finally {
            setWeeklyAdvisoryLoading(false);
        }
    };

    const fetchRecipients = async (country_id, adm3_id) => {
        const headers = getAuthHeaders();
        try {
            const [fRes, oRes] = await Promise.all([
                fetch(`${envConfig.API_URL}/fishermen?country_id=${country_id}&adm3_id=${adm3_id}`, { method: 'GET', headers }),
                fetch(`${envConfig.API_URL}/extoff?country_id=${country_id}&adm3_id=${adm3_id}`, { method: 'GET', headers }),
            ]);
            const fData = await fRes.json().catch(() => ({}));
            const oData = await oRes.json().catch(() => ({}));
            setFarmersList((fData.data || []).map(f => ({ id: f.fisher_id, name: f.name, phone: f.phone })));
            setOfficersList((oData.data || []).map(o => ({ id: o.fisher_id, name: o.name, phone: o.phone, role: o.designation || "Extension Officer" })));
        } catch {
            setFarmersList([]); setOfficersList([]);
        }
        setFarmersSelected(new Set()); setFarmersMode("all"); setFarmersActive(true);
        setOfficersSelected(new Set()); setOfficersMode("all"); setOfficersActive(true);
        setWeeklyFarmersSelected(new Set()); setWeeklyFarmersMode("all"); setWeeklyFarmersActive(true);
        setWeeklyOfficersSelected(new Set()); setWeeklyOfficersMode("all"); setWeeklyOfficersActive(true);
    };

    const toggleId = (setFn, current, id) => {
        const next = new Set(current);
        next.has(id) ? next.delete(id) : next.add(id);
        setFn(next);
    };

    const totalRecipients =
        (farmersActive ? (farmersMode === "all" ? farmersList.length : farmersSelected.size) : 0) +
        (officersActive ? (officersMode === "all" ? officersList.length : officersSelected.size) : 0);

    const weeklyTotalRecipients =
        (weeklyFarmersActive ? (weeklyFarmersMode === "all" ? farmersList.length : weeklyFarmersSelected.size) : 0) +
        (weeklyOfficersActive ? (weeklyOfficersMode === "all" ? officersList.length : weeklyOfficersSelected.size) : 0);

    useEffect(() => {
        if (weeklyAdvisory?.data?.length > 0 && weeklyAdvisory?.week_start) {
            const fmt = d => { const dt = new Date(d); return `${dt.toLocaleString('default', { month: 'short' })} ${dt.getDate()}`; };
            const prefix = `Weekly Advisory for ${fmt(weeklyAdvisory.week_start)} – ${fmt(weeklyAdvisory.week_end)}\n`;
            const text = weeklyAdvisory.data.find(d => d.language === weeklyLanguage)?.advisory_text || '';
            setWeeklyAdvisoryText(prefix + text);
        } else {
            setWeeklyAdvisoryText('');
        }
    }, [weeklyLanguage, weeklyAdvisory]);

    const sendWeeklyMessage = async () => {
        const token = localStorage.getItem("profishBlue_authToken");
        if (!token) {
            Swal.fire({ title: 'Authentication Error', text: 'Authentication token is missing. Please login again.', icon: 'error', confirmButtonText: 'OK' })
                .then(() => { window.location.href = '/login'; });
            return;
        }
        const profishBlue_tokenExpiry = localStorage.getItem("profishBlue_tokenExpiry");
        if (!profishBlue_tokenExpiry || Date.now() > profishBlue_tokenExpiry) {
            logout();
            Swal.fire({ title: 'Authentication Error', text: 'Session expired. Please log in again.', icon: 'error', confirmButtonText: 'OK' })
                .then(() => { window.location.href = '/login'; });
            return;
        }
        const payload = {
            country_id: parseInt(selectedFilters.country_id),
            adm3_id: selectedFilters.adm3_id,
            adm2_id: selectedFilters.adm2_id,
            adm1_id: selectedFilters.adm1_id,
            advisory_text: weeklyAdvisoryText,
            risk_level: weeklyAdvisory?.data?.[0]?.risk_level,
            weekly: true,
            week_start: weeklyAdvisory?.week_start,
            week_end: weeklyAdvisory?.week_end,
            week_pond_temp_min: weeklyAdvisory?.week_pond_temp_min,
            week_pond_temp_max: weeklyAdvisory?.week_pond_temp_max,
            farmers_active: weeklyFarmersActive,
            extoff_active: weeklyOfficersActive,
            farmer_ids: weeklyFarmersMode === "select" && weeklyFarmersSelected.size > 0 ? [...weeklyFarmersSelected] : null,
            extoff_ids: weeklyOfficersMode === "select" && weeklyOfficersSelected.size > 0 ? [...weeklyOfficersSelected] : null,
        };
        setWeeklySending(true);
        try {
            const response = await fetch(`${envConfig.API_URL}/advisory/broadcast`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `${token}` },
                body: JSON.stringify(payload),
            });
            const result = await response.json().catch(() => ({}));
            if (!response.ok) throw { status: response.status, ...result };
            Swal.fire({ title: 'Message', text: result.message || 'Weekly advisory sent successfully.', icon: 'success', confirmButtonText: 'OK' });
            setPageNo(1);
            await fetchMessages();
        } catch (error) {
            Swal.fire({ title: 'Error', text: error?.message || 'Error sending weekly advisory.', icon: 'error', confirmButtonText: 'OK' });
        } finally {
            setWeeklySending(false);
        }
    };

    // Helper function to get the suffix for the day (st, nd, rd, th)
    function getDaySuffix(day) {
        const suffixes = ["th", "st", "nd", "rd"];
        const mod = day % 10;
        const exception = (day % 100 >= 11 && day % 100 <= 13) ? 0 : mod;
        return suffixes[exception] || suffixes[0];
    }

    const sendMessage = async () => {
        const token = localStorage.getItem("profishBlue_authToken");

        if (!token) {
            Swal.fire({
                title: 'Authentication Error',
                text: 'Authentication token is missing. Please login again.',
                icon: 'error',
                confirmButtonText: 'OK',
            }).then(() => {
                window.location.href = '/login';
            });
            return;
        }

        const profishBlue_tokenExpiry = localStorage.getItem("profishBlue_tokenExpiry");
        if (!profishBlue_tokenExpiry || Date.now() > profishBlue_tokenExpiry) {
            logout();
            Swal.fire({
                title: 'Authentication Error',
                text: 'Session expired. Please log in again.',
                icon: 'error',
                confirmButtonText: 'OK',
            }).then(() => {
                window.location.href = '/login';
            });
            return;
        }

        const date = new Date(selectedFilters.date);
        const day = date.getDate();
        const month = date.toLocaleString('default', { month: 'short' });
        const year = date.getFullYear();

        const formattedDate = `${day}${getDaySuffix(day)} ${month} ${year}`;

        let payload = {
            country_id: parseInt(selectedFilters.country_id),
            adm3_id: selectedFilters.adm3_id,
            adm2_id: selectedFilters.adm2_id,
            adm1_id: selectedFilters.adm1_id,
            predicted_pond_temperature: selectedFilters.predicted_pond_temperature,
            risk_level: selectedFilters.risk_level,
            temp_max: selectedFilters.temp_max,
            temp_min: selectedFilters.temp_min,
            advisory_text: advisoryText,
            ref_date: selectedFilters.date,
            farmers_active: farmersActive,
            extoff_active: officersActive,
            farmer_ids: farmersMode === "select" && farmersSelected.size > 0 ? [...farmersSelected] : null,
            extoff_ids: officersMode === "select" && officersSelected.size > 0 ? [...officersSelected] : null,
        };

        try {
            const response = await fetch(`${envConfig.API_URL}/advisory/broadcast`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `${token}`,
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                let errorData = {};
                try {
                    errorData = await response.json();
                } catch (jsonErr) {
                    errorData.message = `Error: ${response.status}`;
                }
                throw { status: response.status, ...errorData };
            }

            const result = await response.json();
            Swal.fire({
                title: 'Message',
                text: result.message || 'Broadcast sent successfully.',
                icon: 'info',
                confirmButtonText: 'OK',
            });

            setPageNo(1);
            await fetchMessages();
        } catch (error) {
            const message =
                error?.message ||
                (error?.status === 404
                    ? error?.message || 'Requested resource not found.'
                    : 'Error submitting request.');

            Swal.fire({
                title: 'Error',
                text: message,
                icon: 'error',
                confirmButtonText: 'OK',
            });
        }
    };

    // Function to export data to Excel
    const exportToExcel = async () => {
        if (!selectedFilters?.country_id) {
            Swal.fire('Info', 'Please select a location to export message logs.', 'info');
            return;
        }

        try {
            const allData = await fetchMessages(true);

            if (!allData.length) {
                Swal.fire('Info', 'No data available to export.', 'info');
                return;
            }

            const exportData = allData.map(row => ({
                'Type': row.weekly && row.week_start ? `Weekly (${fmtShort(row.week_start)} – ${fmtShort(row.week_end)})` : 'Daily',
                'Referred date': formatToHumanDateTime(row.ref_date) || '--',
                'Sent date': row.sent_on ? formatToHumanDateTime(row.sent_on) : '--',
                'Country': selectedFilters.country || '--',
                'Province': row.adm1 || '--',
                'District': row.adm2 || '--',
                [+selectedFilters?.country_id === 1 ? 'EPA' : 'Camp']: row.adm3 || '--',
                'Message': row.advisory_text || '--',
                'Risk Level': row.risk_level || '--',
                'Pond Temperature': row.weekly
                    ? (row.week_pond_temp_min != null ? `${row.week_pond_temp_min}–${row.week_pond_temp_max}°C` : '--')
                    : (row.predicted_pond_temperature != null ? `${row.predicted_pond_temperature}°C` : '--'),
                'Max Air Temperature': row.temp_max !== null && row.temp_max !== undefined ? `${row.temp_max}°C` : '--',
                'Min Air Temperature': row.temp_min !== null && row.temp_min !== undefined ? `${row.temp_min}°C` : '--',
                'Recipient Count': row.recepient_count ?? 'NA',
                'SMS Status': row.pushed_status ?? '--',
                'SMS Fail Reason': row.pushed_status_reason ?? '--',
            }));

            const ws = XLSX.utils.json_to_sheet(exportData);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Message Logs');

            const colWidths = Object.keys(exportData[0]).map((key, i) => ({
                wch: Math.max(
                    key.length,
                    ...exportData.map(row => String(row[key]).length)
                ) + 2,
            }));
            ws['!cols'] = colWidths;

            const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
            XLSX.writeFile(wb, `Message_Logs_${timestamp}.xlsx`);

            Swal.fire('Success', 'Data exported successfully!', 'success');
        } catch (error) {
            Swal.fire('Error', 'Failed to export data. Please try again.', 'error');
        }
    };

    const fmtShort = d => { const dt = new Date(d); return `${dt.toLocaleString('default', { month: 'short' })} ${dt.getDate()}`; };

    // Define table columns
    const columns = [
        {
            name: 'Type',
            selector: (row) => row.weekly ? 'Weekly' : 'Daily',
            cell: (row) => {
                const autoBadge = row.cron_auto
                    ? <><br /><small style={{ padding: '1px 5px', borderRadius: '3px', fontSize: '9px', background: '#1565c0', color: '#fff' }}>Auto</small></>
                    : null;
                return row.weekly && row.week_start
                    ? <span title={`${fmtShort(row.week_start)} – ${fmtShort(row.week_end)}`}>Weekly<br /><small className="text-muted">{fmtShort(row.week_start)} – {fmtShort(row.week_end)}</small>{autoBadge}</span>
                    : <span>Daily{autoBadge}</span>;
            },
            sortable: true,
            width: '130px',
        },
        {
            name: 'Ref. Date',
            selector: (row) => formatToHumanDateTime(row.ref_date) || '--',
            sortable: true,
            width: '110px',
            cell: (row) => <span style={{ whiteSpace: 'normal', overflow: 'visible' }}>{formatToHumanDateTime(row.ref_date) || '--'}</span>,
        },
        {
            name: 'Sent On',
            selector: (row) => formatToHumanDateTime(row.sent_on) || '--',
            sortable: true,
            width: '150px',
            cell: (row) => <span style={{ whiteSpace: 'normal', overflow: 'visible' }}>{formatToHumanDateTime(row.sent_on) || '--'}</span>,
        },
        {
            name: 'Country',
            selector: () => selectedFilters.country || '--',
            sortable: true,
            width: '90px',
        },
        {
            name: 'Province',
            selector: (row) => row.adm1 || '--',
            sortable: true,
            width: '100px',
        },
        {
            name: 'District',
            selector: (row) => row.adm2 || '--',
            sortable: true,
            width: '100px',
        },
        {
            name: +selectedFilters?.country_id === 1 ? 'EPA' : 'Camp',
            selector: (row) => row.adm3 || '--',
            sortable: true,
            width: '100px',
        },
        {
            name: 'Message',
            selector: (row) => row.advisory_text || '--',
            sortable: true,
            grow: 2,
            cell: (row) => (
                <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '12px', lineHeight: '1.3' }}>
                    {row.advisory_text || '--'}
                </div>
            ),
        },
        {
            name: 'Risk Level',
            selector: (row) => row.risk_level ?? '--',
            sortable: true,
            width: '130px',
        },
        {
            name: 'Pond Temp',
            selector: (row) => row.weekly
                ? (row.week_pond_temp_min != null ? `${row.week_pond_temp_min}–${row.week_pond_temp_max}°C` : '--')
                : (row.predicted_pond_temperature != null ? `${row.predicted_pond_temperature}°C` : '--'),
            sortable: true,
            width: '120px',
        },
        {
            name: 'Max Temp',
            selector: (row) => `${row.temp_max ?? '--'}°C`,
            sortable: true,
            width: '100px',
        },
        {
            name: 'Min Temp',
            selector: (row) => `${row.temp_min ?? '--'}°C`,
            sortable: true,
            width: '100px',
        },
        {
            name: 'Recipients',
            selector: (row) => row.recepient_count ?? 'NA',
            sortable: true,
            width: '110px',
            cell: (row) => <RecipientsCell row={row} />,
        },
        {
            name: 'SMS Status',
            selector: (row) => row.pushed_status ?? '--',
            sortable: true,
            width: '110px',
            cell: (row) => {
                if (row.pushed_status === 'sent') return <span style={{ padding: '2px 6px', borderRadius: '3px', background: '#2e7d32', color: '#fff', fontSize: '11px' }}>Sent</span>;
                if (row.pushed_status === 'failed') return <span title={row.pushed_status_reason || ''} style={{ padding: '2px 6px', borderRadius: '3px', background: '#c62828', color: '#fff', fontSize: '11px', cursor: 'help' }}>Failed</span>;
                return <span style={{ color: '#999', fontSize: '11px' }}>--</span>;
            },
        },
    ];

    const handlePageChange = (page) => {
        setPageNo(page);
    };

    const handlePerRowsChange = (newPerPage, page) => {
        setPerPage(newPerPage);
        setPageNo(page);
    };

    const handleRowSelected = ({ selectedRows }) => {
        setSelectedRows(selectedRows);
    };

    const customStyles = {
        table: {
            style: {
                border: '1px solid #dee2e6',
            },
        },
        headRow: {
            style: {
                backgroundColor: '#2e7d32',
                color: '#ffffff',
                fontWeight: 'bold',
                borderBottom: '1px solid #dee2e6',
            },
        },
        headCells: {
            style: {
                padding: '10px 5px!important',
                borderRight: '1px solid #dee2e6',
            },
        },
        cells: {
            style: {
                padding: '5px 5px!important',
                borderRight: '1px solid #dee2e6',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
            },
        },
        rows: {
            style: {
                maxHeight: '80px',
                '&:hover': {
                    backgroundColor: '#f1f1f1',
                },
            },
        },
        pagination: {
            style: {
                padding: '4px 8px',
                minHeight: '40px',
                border: 'none',
                padding: '5px 5px 5px 5px',
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                backgroundColor: '#e8f5e9',
            },
            pageButtonsStyle: {
                backgroundColor: 'transparent',
                border: 'none',
                color: '#000',
                padding: '0.25rem 0.5rem',
                margin: '0 0.25rem',
                cursor: 'pointer',
                '&:hover': {
                    backgroundColor: '#d4edda',
                },
                '&:disabled': {
                    opacity: 0.5,
                    cursor: 'not-allowed',
                },
            },
        },
    };

    // Handle language change
    const handleLanguageChange = (language) => {
        setSelectedLanguage(language);
    };

    return (
        <>
            <div className="filter-box pt-3 border-right-0" style={{ borderRight: '0px', marginBottom: '130px' }}>
                <div className="row px-0">
                    <div className="col-sm-12 col-md-12 col-lg-12 px-0">
                        <h5>Current Week Advisory</h5>
                        <div className="row">
                            <div className="col-12">
                                <div className="bg-light pt-3 pb-3 rounded">
                                    <div className="row align-items-center">
                                        <div className="col-12 col-md-3 text-center mb-3 mb-md-0">
                                            {selectedFilters?.selectedSpecies && (
                                                <img
                                                    src={`assets/images/species-${selectedFilters?.country_id}-${selectedFilters.selectedSpecies?.code}.jpg`}
                                                    className="img-fluid rounded"
                                                    style={{ maxHeight: "155px", objectFit: "cover" }}
                                                    loading="lazy"
                                                    alt="Species"
                                                />
                                            )}
                                        </div>
                                        <div className="col-12 col-md-9">
                                            <div className="row">
                                                <div className="col-12 col-md-2 text-center text-md-start mb-3 mb-md-0 px-0">
                                                    <h6 className="fw-bold mt-1">Species</h6>
                                                    <p className="mb-1 mt-2 pt-1">{selectedFilters?.selectedSpecies?.scientific_name || "--"}</p>
                                                    <p className="" style={{ color: '#1A4985', fontWeight: '500' }}>{selectedFilters?.selectedSpecies?.common_name || "--"}</p>
                                                </div>
                                                <div className="col-sm-12 col-md-10 col-lg-10">
                                                    <div className="table-responsive">
                                                        <table className="table minMaxTable">
                                                            <thead>
                                                                <tr>
                                                                    <th></th>
                                                                    <th className="fw-bold">Max. Air Temperature</th>
                                                                    <th className="fw-bold">Min. Air Temperature</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                <tr>
                                                                    <td className="small text-muted mb-0">{formatToHumanDateTime(selectedFilters?.ref_date)}</td>
                                                                    <td><p className="fw-semibold mb-0">{`${maxTemp ?? "--"}°C`}</p></td>
                                                                    <td><p className="fw-semibold mb-0">{`${minTemp ?? "--"}°C`}</p></td>
                                                                </tr>
                                                                <tr>
                                                                    <td className="small text-muted mb-0">Last week (Avg.)</td>
                                                                    <td><p className="fw-semibold mb-0">{`${selectedFilters?.histoircMaxTemp ?? "--"}°C`}</p></td>
                                                                    <td><p className="fw-semibold mb-0">{`${selectedFilters?.histoircMinTemp ?? "--"}°C`}</p></td>
                                                                </tr>
                                                                <tr>
                                                                    <td className="small text-muted mb-0">Next week (Avg.)</td>
                                                                    <td><p className="fw-semibold mb-0">{`${selectedFilters?.forecastMaxTemp ?? "--"}°C`}</p></td>
                                                                    <td><p className="fw-semibold mb-0">{`${selectedFilters?.forecastMinTemp ?? "--"}°C`}</p></td>
                                                                </tr>
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-sm-12 col-md-12 col-lg-12 mt-3">
                                <div className="bg-light-box p-18px">
                                    <div className="row">
                                        <div className="col-sm-12 col-md-4 col-lg-4" style={{ borderRight: '2px solid #00000030' }}>
                                            <p className="">Risk Level</p>
                                            <p className="numberText mb-0" style={{ fontWeight: '600', color: '#212529bf', fontSize: '18px' }}>{`${selectedFilters?.risk_level ?? '--'}`}</p>
                                        </div>
                                        <div className="col-sm-12 col-md-4 col-lg-4">
                                            <p className="">Predicted Pond Temperature</p>
                                            <p className="numberText mb-0" style={{ fontWeight: '600', color: '#212529bf', fontSize: '18px' }}>{`${selectedFilters?.predicted_pond_temperature ?? '--'}°C`}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-sm-12 col-md-12 col-lg-12 mt-3">
                                <div className="bg-light-box p-18px">
                                    {/* Language Selection Radio Buttons */}
                                    <div className="d-flex mb-3">
                                        <div className="form-check me-3">
                                            <input
                                                type="radio"
                                                className="form-check-input"
                                                id="english"
                                                name="language"
                                                value="English"
                                                checked={selectedLanguage === 'English'}
                                                onChange={() => handleLanguageChange('English')}
                                                disabled={!advisoryData}
                                            />
                                            <label className="form-check-label" htmlFor="english">English</label>
                                        </div>
                                        <div className="form-check me-3">
                                            <input
                                                type="radio"
                                                className="form-check-input"
                                                id="localLanguage"
                                                name="language"
                                                value={localLanguageLabel}
                                                checked={selectedLanguage === localLanguageLabel}
                                                onChange={() => handleLanguageChange(localLanguageLabel)}
                                                disabled={!advisoryData}
                                            />
                                            <label className="form-check-label" htmlFor="localLanguage">{localLanguageLabel}</label>
                                        </div>
                                        {+selectedFilters?.country_id !== 1 && (
                                            <div className="form-check">
                                                <input
                                                    type="radio"
                                                    className="form-check-input"
                                                    id="chichewa"
                                                    name="language"
                                                    value="Chichewa"
                                                    checked={selectedLanguage === 'Chichewa'}
                                                    onChange={() => handleLanguageChange('Chichewa')}
                                                    disabled={!advisoryData}
                                                />
                                                <label className="form-check-label" htmlFor="chichewa">Chichewa</label>
                                            </div>
                                        )}
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center mt-3 mb-3">
                                        <div className="pe-3 w-100"><p className="mb-0">You may modify/rewrite the advisory before sending.</p></div>
                                        <div><button className="btn btn-send" onClick={sendMessage} disabled={!advisoryData}>Send</button></div>
                                    </div>
                                    <div className="box">
                                        <textarea
                                            className="centerText"
                                            style={{
                                                whiteSpace: 'pre-line',
                                                width: '100%',
                                                border: 'none',
                                                resize: 'none',
                                                background: 'transparent',
                                                fontSize: '14px',
                                                fontWeight: '400',
                                            }}
                                            rows={5}
                                            value={advisoryText || ''}
                                            onChange={(e) => setAdvisoryText(e.target.value)}
                                        />
                                    </div>
                                    <div className="mt-3">
                                        <p className="mb-2" style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#6B7686' }}>Recipients</p>
                                        <div style={{ display: 'flex', flexDirection: 'row', gap: 10 }}>
                                            <RecipientGroupCard
                                                icon="🐟" title="Fish Farmers" subtitle="Registered pond operators in this camp"
                                                mode={farmersMode} setMode={setFarmersMode}
                                                count={farmersSelected.size} total={farmersList.length}
                                                active={farmersActive} setActive={setFarmersActive}
                                                onOpenPicker={() => setModalOpen("farmers")}
                                            />
                                            <RecipientGroupCard
                                                icon="🧑‍🌾" title="Extension Officers" subtitle="District & field support staff"
                                                mode={officersMode} setMode={setOfficersMode}
                                                count={officersSelected.size} total={officersList.length}
                                                active={officersActive} setActive={setOfficersActive}
                                                onOpenPicker={() => setModalOpen("officers")}
                                            />
                                        </div>
                                        <p className="mt-2 mb-0" style={{ fontSize: 12.5, color: '#6B7686' }}>
                                            <strong>{totalRecipients}</strong> recipient{totalRecipients !== 1 ? 's' : ''} will receive this advisory
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <hr className="my-3" />

                    {/* ── Weekly Advisory ── */}
                    <div className="col-sm-12 col-md-12 col-lg-12 px-0 mb-4">
                        <h5 className="mb-2">
                            Weekly Advisory
                            {weeklyAdvisory?.week_start && (
                                <span className="text-muted small ms-1">
                                    {(() => {
                                        const fmt = d => { const dt = new Date(d); return `${dt.toLocaleString('default', { month: 'short' })} ${dt.getDate()}`; };
                                        return `(${fmt(weeklyAdvisory.week_start)} – ${fmt(weeklyAdvisory.week_end)})`;
                                    })()}
                                </span>
                            )}
                        </h5>

                        {weeklyAdvisoryLoading ? (
                            <div className="text-center p-3 text-muted">Loading weekly advisory...</div>
                        ) : weeklyAdvisory?.data?.length > 0 ? (
                            <>
                                {weeklyAdvisory.data[0]?.risk_level && (
                                    <div className="bg-light-box p-18px mb-3">
                                        <div className="row">
                                            <div className="col-sm-12 col-md-4 col-lg-4">
                                                <p className="">Weekly Risk Level</p>
                                                <p className="numberText mb-0" style={{ fontWeight: '600', color: '#212529bf', fontSize: '18px' }}>{weeklyAdvisory.data[0].risk_level}</p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            <div className="bg-light-box p-18px">
                                {/* Language radio buttons */}
                                <div className="d-flex mb-3">
                                    {[...weeklyAdvisory.data].sort((a, b) => a.language === 'english' ? -1 : b.language === 'english' ? 1 : 0).map(record => (
                                        <div className="form-check me-3" key={record.language}>
                                            <input
                                                type="radio"
                                                className="form-check-input"
                                                id={`weekly-${record.language}`}
                                                name="weeklyLanguage"
                                                value={record.language}
                                                checked={weeklyLanguage === record.language}
                                                onChange={() => setWeeklyLanguage(record.language)}
                                            />
                                            <label className="form-check-label" htmlFor={`weekly-${record.language}`}>
                                                {record.language === 'english' ? 'English'
                                                    : record.language === 'bemba' ? 'Bemba'
                                                    : 'Chichewa'}
                                            </label>
                                        </div>
                                    ))}
                                </div>

                                {/* Advisory text */}
                                <div className="d-flex justify-content-between align-items-center mt-3 mb-3">
                                    <div className="pe-3 w-100"><p className="mb-0">You may modify/rewrite the advisory before sending.</p></div>
                                    <div><button className="btn btn-send" onClick={sendWeeklyMessage} disabled={!weeklyAdvisoryText || weeklySending}>{weeklySending ? 'Sending...' : 'Send'}</button></div>
                                </div>
                                <div className="box">
                                    <textarea
                                        className="centerText"
                                        style={{
                                            whiteSpace: 'pre-line',
                                            width: '100%',
                                            border: 'none',
                                            resize: 'none',
                                            background: 'transparent',
                                            fontSize: '14px',
                                            fontWeight: '400',
                                        }}
                                        rows={5}
                                        value={weeklyAdvisoryText}
                                        onChange={e => setWeeklyAdvisoryText(e.target.value)}
                                    />
                                </div>
                                <div className="mt-3">
                                    <p className="mb-2" style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#6B7686' }}>Recipients</p>
                                    <div style={{ display: 'flex', flexDirection: 'row', gap: 10 }}>
                                        <RecipientGroupCard
                                            icon="🐟" title="Fish Farmers" subtitle="Registered pond operators in this camp"
                                            mode={weeklyFarmersMode} setMode={setWeeklyFarmersMode}
                                            count={weeklyFarmersSelected.size} total={farmersList.length}
                                            active={weeklyFarmersActive} setActive={setWeeklyFarmersActive}
                                            onOpenPicker={() => setWeeklyModalOpen("farmers")}
                                        />
                                        <RecipientGroupCard
                                            icon="🧑‍🌾" title="Extension Officers" subtitle="District & field support staff"
                                            mode={weeklyOfficersMode} setMode={setWeeklyOfficersMode}
                                            count={weeklyOfficersSelected.size} total={officersList.length}
                                            active={weeklyOfficersActive} setActive={setWeeklyOfficersActive}
                                            onOpenPicker={() => setWeeklyModalOpen("officers")}
                                        />
                                    </div>
                                    <p className="mt-2 mb-0" style={{ fontSize: 12.5, color: '#6B7686' }}>
                                        <strong>{weeklyTotalRecipients}</strong> recipient{weeklyTotalRecipients !== 1 ? 's' : ''} will receive this advisory
                                    </p>
                                </div>
                            </div>
                            </>
                        ) : (
                            <div className="text-center p-3 text-muted" style={{ fontSize: '14px' }}>
                                {selectedFilters?.adm3_id
                                    ? 'No weekly advisory logged yet for this camp.'
                                    : 'Choose a location to view the weekly advisory.'}
                            </div>
                        )}
                    </div>

                    <hr className="my-3" />
                    <div className="col-sm-12 col-md-12 col-lg-12 px-0" style={{ marginBottom: "50px" }}>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <div>
                                <h5>Recent Message Logs</h5>
                                {selectedRows.length > 0 && (
                                    <p className="mb-0">
                                        {selectedRows.length} row(s) selected
                                    </p>
                                )}
                            </div>
                            <div>
                                <button
                                    className="btn btn-send me-2"
                                    onClick={() => navigate('/message-logs')}
                                    style={{
                                        backgroundColor: '#2e7d32',
                                        color: '#fff',
                                        padding: '8px 5px',
                                        borderRadius: '4px',
                                        border: 'none',
                                        cursor: 'pointer',
                                        opacity: 1,
                                        width: '120px',
                                    }}
                                >
                                    Go to all logs
                                </button>
                                <button
                                    className="btn btn-send"
                                    onClick={exportToExcel}
                                    disabled={!selectedFilters?.country_id}
                                    style={{
                                        backgroundColor: '#2e7d32',
                                        color: '#fff',
                                        padding: '8px 5px',
                                        borderRadius: '4px',
                                        border: 'none',
                                        cursor: selectedFilters?.country_id ? 'pointer' : 'not-allowed',
                                        opacity: selectedFilters?.country_id ? 1 : 0.6,
                                    }}
                                >
                                    Export Data
                                </button>
                            </div>
                        </div>
                        <DataTable
                            className="scrollY p-1"
                            columns={columns}
                            data={recentMessageLogs}
                            // selectableRows
                            // onSelectedRowsChange={handleRowSelected}
                            pagination
                            paginationServer
                            paginationTotalRows={totalRows}
                            onChangePage={handlePageChange}
                            onChangeRowsPerPage={handlePerRowsChange}
                            paginationRowsPerPageOptions={[5, 10, 25, 50]}
                            paginationPerPage={perPage}
                            paginationComponentOptions={{
                                rowsPerPageText: 'Records per page:',
                                rangeSeparatorText: 'of',
                                selectAllRowsItem: false,
                            }}
                            customStyles={customStyles}
                            noDataComponent={<div className="text-center p-2 font-14px">Choose a location to view the message logs</div>}
                            highlightOnHover
                            dense
                        />
                    </div>
                </div>
            </div>
        {modalOpen === "farmers" && (
            <RecipientModal
                title="Select Fish Farmers" people={farmersList} fields="farmers"
                selectedIds={farmersSelected}
                onToggle={(id) => toggleId(setFarmersSelected, farmersSelected, id)}
                onSelectAll={(ids) => {
                    const allSelected = ids.every(id => farmersSelected.has(id));
                    setFarmersSelected(allSelected ? new Set() : new Set([...farmersSelected, ...ids]));
                }}
                onClear={() => setFarmersSelected(new Set())}
                onClose={() => setModalOpen(null)}
                onDone={() => setModalOpen(null)}
            />
        )}
        {modalOpen === "officers" && (
            <RecipientModal
                title="Select Extension Officers" people={officersList} fields="officers"
                selectedIds={officersSelected}
                onToggle={(id) => toggleId(setOfficersSelected, officersSelected, id)}
                onSelectAll={(ids) => {
                    const allSelected = ids.every(id => officersSelected.has(id));
                    setOfficersSelected(allSelected ? new Set() : new Set([...officersSelected, ...ids]));
                }}
                onClear={() => setOfficersSelected(new Set())}
                onClose={() => setModalOpen(null)}
                onDone={() => setModalOpen(null)}
            />
        )}
        {weeklyModalOpen === "farmers" && (
            <RecipientModal
                title="Select Fish Farmers" people={farmersList} fields="farmers"
                selectedIds={weeklyFarmersSelected}
                onToggle={(id) => toggleId(setWeeklyFarmersSelected, weeklyFarmersSelected, id)}
                onSelectAll={(ids) => {
                    const allSelected = ids.every(id => weeklyFarmersSelected.has(id));
                    setWeeklyFarmersSelected(allSelected ? new Set() : new Set([...weeklyFarmersSelected, ...ids]));
                }}
                onClear={() => setWeeklyFarmersSelected(new Set())}
                onClose={() => setWeeklyModalOpen(null)}
                onDone={() => setWeeklyModalOpen(null)}
            />
        )}
        {weeklyModalOpen === "officers" && (
            <RecipientModal
                title="Select Extension Officers" people={officersList} fields="officers"
                selectedIds={weeklyOfficersSelected}
                onToggle={(id) => toggleId(setWeeklyOfficersSelected, weeklyOfficersSelected, id)}
                onSelectAll={(ids) => {
                    const allSelected = ids.every(id => weeklyOfficersSelected.has(id));
                    setWeeklyOfficersSelected(allSelected ? new Set() : new Set([...weeklyOfficersSelected, ...ids]));
                }}
                onClear={() => setWeeklyOfficersSelected(new Set())}
                onClose={() => setWeeklyModalOpen(null)}
                onDone={() => setWeeklyModalOpen(null)}
            />
        )}
        </>
    );
}

export default React.memo(HomeDetails);